// Username prompt — the INVITEE onboarding step (INVITE-ONBOARDING-
// USERNAME-NOT-ORG-1). A user who joined via an invite must never be
// pushed into org creation (they just joined one); instead they get this
// LIGHT, dismissable nudge to pick their handle — the username the server
// auto-derived from their email until now.
//
// Mirrors createOrgModal.js's open/close mechanics (@symbo.ls/windows
// custom-template layer via openCustomModal) and its localStorage
// persistence convention (symbols_org_onboarding_skipped) so the prompt
// shows at most once per user+browser, however it is resolved.
//
// Trigger contract: the accept-invite page raises `_usernamePromptPending`
// on root state after a live (no-reload) switch, or persists the PENDING
// key before its hard-reload fallback; UsernamePromptGate watches both and
// opens the layer once. Skip and save both persist the DONE key.

const USERNAME_PROMPT_LAYER_ID = 'username-prompt'
const USERNAME_PROMPT_DONE_KEY = 'symbols_username_prompt_done'
const USERNAME_PROMPT_PENDING_KEY = 'symbols_username_prompt_pending'

export const isUsernamePromptDone = () => {
  try {
    return (
      typeof localStorage !== 'undefined' && localStorage.getItem(USERNAME_PROMPT_DONE_KEY) === '1'
    )
  } catch {
    return false
  }
}

export const isUsernamePromptPendingPersisted = () => {
  try {
    return (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(USERNAME_PROMPT_PENDING_KEY) === '1'
    )
  } catch {
    return false
  }
}

// Function declaration ON PURPOSE — context registration exposes it to
// el.call, which is how the shared-package accept-invite page (import
// boundaries forbid a cross-package import) persists the marker before
// its hard-reload fallback.
export function persistUsernamePromptPending() {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(USERNAME_PROMPT_PENDING_KEY, '1')
    }
  } catch {
    /* pending marker is best-effort — the live-path root flag still works */
  }
}

const _markDone = () => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(USERNAME_PROMPT_DONE_KEY, '1')
      localStorage.removeItem(USERNAME_PROMPT_PENDING_KEY)
    }
  } catch {
    /* the root flag below still stops re-opens this session */
  }
}

const _rootState = (el) =>
  el?.state?.root || el?.getRoot?.()?.state?.root || el?.getRoot?.()?.state || null

const _usernamePromptResetFields = {
  _unamePromptDraft: '',
  _unamePromptBusy: false,
  _unamePromptError: null,
  _unamePromptStatus: 'idle',
  _unamePromptReason: null
}

export function openUsernamePrompt() {
  const root = _rootState(this)
  if (!root) return
  root.update({
    ..._usernamePromptResetFields,
    _unamePromptDraft: root.currentUserUsername || '',
    _usernamePromptPending: false
  })
  // Dismissable ON PURPOSE — this is a nudge, not a gate ("slightly ask",
  // Nika 2026-08-26). Backdrop/ESC close counts as skip-for-now (no DONE
  // key), so it can return next boot only while the PENDING key stands.
  this.call('openCustomModal', {
    id: USERNAME_PROMPT_LAYER_ID,
    content: { extends: 'AppUsernamePrompt' }
  })
}

export function closeUsernamePrompt() {
  const root = _rootState(this)
  root?.update(_usernamePromptResetFields)
  this.call('closeModal', USERNAME_PROMPT_LAYER_ID)
}

// "Keep current" — an explicit choice: persist DONE so the nudge never
// returns, keep the derived username.
export function skipUsernamePrompt() {
  _markDone()
  const root = _rootState(this)
  root?.update({ ..._usernamePromptResetFields, _usernamePromptPending: false })
  this.call('closeModal', USERNAME_PROMPT_LAYER_ID)
}

export async function saveUsernamePrompt() {
  const root = _rootState(this)
  if (!root || root._unamePromptBusy) return
  const draft = String(root._unamePromptDraft || '')
    .trim()
    .toLowerCase()
  if (!draft) {
    // KEY-IN-STATE: the Hint resolves `_unamePromptError` through polyglot, which
    // returns a server-supplied message (the `msg` fallback below) verbatim.
    root.update({ _unamePromptError: 'workspace.username.error.required' })
    return
  }
  if (draft === (root.currentUserUsername || '')) {
    // No change — same outcome as an explicit keep.
    skipUsernamePrompt.call(this)
    return
  }
  root.update({ _unamePromptBusy: true, _unamePromptError: null })
  try {
    const sdk = this.sdk || this.getRoot?.()?.sdk
    await sdk.updateMe({ username: draft })
    _markDone()
    root.update({
      ..._usernamePromptResetFields,
      currentUserUsername: draft,
      _usernamePromptPending: false
    })
    this.call('closeModal', USERNAME_PROMPT_LAYER_ID)
  } catch (err) {
    const msg = String(err?.message || '')
    root.update({
      _unamePromptBusy: false,
      _unamePromptError: /taken|exists|unavailable|duplicate/i.test(msg)
        ? 'workspace.username.taken'
        : msg || 'workspace.username.error.saveFailed'
    })
  }
}
